import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';

const BASE_URL = 'https://api.deepnets.ai';
const AGENTCASH_URL = 'https://agentcash.dev';
const X402_URL = 'https://x402.org';
const API_DOCS_URL = '/api-docs';

const CodeBlock: React.FC<{ children: string }> = ({ children }) => (
  <pre className="bg-crypto-dark border border-gray-800 rounded-lg p-4 overflow-x-auto text-sm font-mono text-gray-200">
    <code>{children}</code>
  </pre>
);

const UseCase: React.FC<{ icon: string; color: string; title: string; children: React.ReactNode }> = ({ icon, color, title, children }) => (
  <div className="bg-crypto-gray/40 border border-gray-800 rounded-lg p-5">
    <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg mb-3 ${color}`}>
      <i className={`fas ${icon} text-sm`} />
    </div>
    <div className="font-semibold text-white mb-1">{title}</div>
    <p className="text-gray-400 text-sm">{children}</p>
  </div>
);

const AgentsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
      <Helmet>
        <title>For Agents — Deepnets.ai</title>
        <meta
          name="description"
          content="Deepnets.ai for AI agents: pay-per-call Solana token safety at $0.01, no accounts, powered by x402 and Coinbase's facilitator. Plug into Claude, Codex, or any x402-capable agent."
        />
      </Helmet>
      <Header />
      <main className="flex-1 max-w-4xl mx-auto px-6 py-12 w-full">
        {/* Hero */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-semibold bg-crypto-accent/10 border border-crypto-accent/30 text-crypto-accent mb-4">
            <i className="fas fa-bolt text-[10px]" />
            NEW — x402 PAYMENTS LIVE
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Deepnets for AI Agents</h1>
          <p className="text-gray-400 text-lg leading-relaxed">
            Stop your agents from trading rugs. Pay <span className="text-white font-medium">$0.01</span> per token-safety check, settled on-chain in a single call. No signup, no API keys, no credit top-ups — just USDC on Solana via the{' '}
            <a href={X402_URL} target="_blank" rel="noopener noreferrer" className="text-crypto-accent hover:text-white">x402 protocol</a>.
          </p>
        </div>

        {/* AgentCash quickstart — the star of the show */}
        <div className="bg-gradient-to-br from-crypto-accent/15 via-crypto-gray/40 to-crypto-gray/40 border border-crypto-accent/40 rounded-xl p-6 mb-10">
          <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">30-second setup</div>
              <h2 className="text-xl font-bold text-white">Add Deepnets to your agent with AgentCash</h2>
            </div>
            <a
              href={AGENTCASH_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-crypto-accent hover:text-white font-medium whitespace-nowrap"
            >
              agentcash.dev →
            </a>
          </div>
          <p className="text-gray-300 text-sm mb-4">
            <a href={AGENTCASH_URL} target="_blank" rel="noopener noreferrer" className="text-crypto-accent hover:text-white">AgentCash</a>{' '}
            is the simplest way to wire an x402-paid API into Claude, Codex, or any agent runtime — one balance, every API on the internet. They're even{' '}
            <span className="text-white font-medium">funding new sign-ups with starter cash</span>, so you can try Deepnets without spending anything of your own.
          </p>
          <CodeBlock>
{`npx agentcash add https://api.deepnets.ai`}
          </CodeBlock>
          <div className="text-xs text-gray-500 mt-3">
            That's it. Your agent now knows how to call Deepnets and the payments settle automatically from your AgentCash balance.
          </div>
        </div>

        {/* Why it's agent-shaped */}
        <h2 className="text-xl font-semibold mb-4">Why agents like this</h2>
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <UseCase icon="fa-shield-halved" color="bg-green-500/15 text-green-300" title="Pre-flight rug detection">
            Before your trading bot buys, hit <code className="font-mono text-xs">/api/token-safety?mint=…</code>. 1¢ to skip a DANGEROUS or RISKY token saves a lot more than 1¢.
          </UseCase>
          <UseCase icon="fa-layer-group" color="bg-blue-500/15 text-blue-300" title="Wallet-network checks">
            Pull the funding graph behind a token's holders. Insider clusters, sybil wallets, and exchange-funded bundles, all as structured JSON.
          </UseCase>
          <UseCase icon="fa-list-check" color="bg-purple-500/15 text-purple-300" title="Never waste cycles">
            Poll the curated <code className="font-mono text-xs">/api/watchlist</code> so your agent only looks at high-volume tokens that already passed a safety screen.
          </UseCase>
        </div>

        {/* Pricing */}
        <h2 className="text-xl font-semibold mb-3">Pricing</h2>
        <p className="text-gray-400 text-sm mb-4">
          USDC on Solana mainnet. Payments go to the deepnets treasury. The facilitator (Coinbase CDP) sponsors SOL gas, so a USDC balance is all you need.
        </p>
        <div className="bg-crypto-gray/40 border border-gray-800 rounded-lg overflow-hidden mb-10">
          <table className="w-full text-sm">
            <thead className="bg-crypto-dark text-gray-400 uppercase text-xs">
              <tr>
                <th className="text-left px-5 py-3">Tier</th>
                <th className="text-left px-5 py-3">Price</th>
                <th className="text-left px-5 py-3">Endpoints</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              <tr>
                <td className="px-5 py-3 font-medium text-white whitespace-nowrap">Basic read</td>
                <td className="px-5 py-3 font-mono text-gray-200">$0.01</td>
                <td className="px-5 py-3 text-gray-400">
                  token-safety, watchlist, trending, flagged-tokens, social-research, streamflow-locks, tibane-staking, token-safety-history, token stats + holder-graph, wallet-details, network-details
                </td>
              </tr>
              <tr>
                <td className="px-5 py-3 font-medium text-white whitespace-nowrap">Holder analysis</td>
                <td className="px-5 py-3 font-mono text-gray-200">$0.15</td>
                <td className="px-5 py-3 text-gray-400">
                  /api/token-details?mint=…, /api/holder-analysis?mint=… — full holder + network breakdown
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Manual example */}
        <h2 className="text-xl font-semibold mb-3">Prefer a raw x402 client?</h2>
        <p className="text-gray-400 text-sm mb-4">
          If your agent already speaks x402, point it at{' '}
          <code className="font-mono text-crypto-accent">{BASE_URL}</code>. The first unpaid request returns <code className="font-mono text-xs text-gray-300">402 Payment Required</code> with a <code className="font-mono text-xs text-gray-300">PAYMENT-REQUIRED</code> header; your client signs a USDC transfer and retries with <code className="font-mono text-xs text-gray-300">X-PAYMENT</code>.
        </p>
        <CodeBlock>
{`# Inspect the 402:
curl -i 'https://api.deepnets.ai/api/token-safety?mint=<MINT>'

# From Node — @x402/core/client + @solana/kit handle the
# 402 → sign → retry dance automatically.`}
        </CodeBlock>

        {/* Full docs CTA */}
        <div className="bg-crypto-accent/10 border border-crypto-accent/30 rounded-lg p-5 mt-10 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-semibold text-white mb-1">Full endpoint reference</div>
            <p className="text-gray-400 text-sm">
              Schemas, response shapes, every route. Also covers the API-key path if you prefer a deepnets account.
            </p>
          </div>
          <Link
            to={API_DOCS_URL}
            className="bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-5 py-2.5 rounded-lg font-medium whitespace-nowrap"
          >
            API docs →
          </Link>
        </div>

        <div className="text-center text-gray-500 text-sm pt-8 mt-8 border-t border-gray-800">
          Building something? Drop us a line at{' '}
          <a href="mailto:support@deepnets.ai" className="text-crypto-accent hover:text-white">
            support@deepnets.ai
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AgentsPage;
